import React, { useEffect, useState } from "react";
import {
  buildDiagnosticReport,
  clearDiagnosticLog,
  getDiagnosticLog,
  type DiagEntry,
} from "../lib/diagnostics";

interface DiagnosticsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type CopyState = "idle" | "copied" | "failed";

export function DiagnosticsDrawer({ isOpen, onClose }: DiagnosticsDrawerProps) {
  const [report, setReport] = useState<string>("");
  const [entries, setEntries] = useState<DiagEntry[]>([]);
  const [copyState, setCopyState] = useState<CopyState>("idle");

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const text = await buildDiagnosticReport();
      if (cancelled) return;
      setReport(text);
      setEntries(getDiagnosticLog());
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    // navigator.clipboard.writeText requires a user gesture + secure context
    // on iOS Safari. It works from a tap handler like this one.
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(report);
        setCopyState("copied");
        setTimeout(() => setCopyState("idle"), 2500);
        return;
      }
      throw new Error("clipboard API unavailable");
    } catch {
      setCopyState("failed");
      setTimeout(() => setCopyState("idle"), 2500);
    }
  };

  const handleClear = () => {
    clearDiagnosticLog();
    setEntries([]);
    // Rebuild so the textarea shows empty-log state.
    buildDiagnosticReport().then(setReport);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-60"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-lg rounded-t-lg shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Diagnostics</h2>
            <p className="text-xs text-gray-500">
              {entries.length === 0
                ? "No errors logged yet."
                : `${entries.length} error${entries.length === 1 ? "" : "s"} logged.`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-4 py-3 border-b border-gray-200">
          <p className="text-sm text-gray-700 mb-2">
            Tap <span className="font-semibold">Copy</span> to copy the report, then paste it
            back to the developer. If Copy fails, long-press inside the box below, choose
            &ldquo;Select All&rdquo;, then &ldquo;Copy&rdquo;.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-3 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors text-sm font-medium"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              {copyState === "copied"
                ? "Copied"
                : copyState === "failed"
                  ? "Copy failed — select manually below"
                  : "Copy diagnostics"}
            </button>
            <button
              onClick={handleClear}
              className="flex items-center gap-1 px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm"
            >
              Clear log
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 py-3">
          <textarea
            readOnly
            value={report}
            className="w-full h-full min-h-[240px] font-mono text-xs p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-800 resize-none"
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
      </div>
    </div>
  );
}
